import { useRef, useEffect } from 'react';
import { AreaChart, Area, ResponsiveContainer, Tooltip } from 'recharts';
import { fmt } from '../../lib/portfolio';

const shortM = iso => new Date(iso + 'T00:00:00Z').toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });

const SparkTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div style={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '7px', padding: '5px 9px', fontSize: '11px' }}>
      <span style={{ color: 'rgba(255,255,255,0.55)' }}>{shortM(p.month)} · </span>
      <span style={{ color: '#f5f0e8', fontWeight: 600 }}>{fmt(p.value)}</span>
    </div>
  );
};

export default function TileCard({ label, value, subValue, badge, sparkData = [], color = '#4da6ff', glow = 'rgba(77,166,255,0.22)', green = false }) {
  const gid = `spark-${label.replace(/\s+/g, '')}`;
  const firstRender = useRef(true);
  useEffect(() => { firstRender.current = false; }, []);
  return (
    <div
      className="dash-card relative rounded-2xl p-5 overflow-hidden flex flex-col justify-between"
      style={green ? {
        background: 'linear-gradient(135deg, rgba(52,211,153,0.28), rgba(52,211,153,0.08))',
        border: '1px solid rgba(52,211,153,0.45)',
        boxShadow: 'var(--card-shadow)',
        backdropFilter: 'blur(14px)',
        '--card-accent': color, '--card-accent-glow': glow,
      } : {
        background: 'var(--card-grad)',
        border: '1px solid var(--card-border)',
        boxShadow: 'var(--card-shadow)',
        backdropFilter: 'blur(14px)',
        '--card-accent': color, '--card-accent-glow': glow,
      }}
    >
      <div className="card-glow" style={{ position: 'absolute', top: '-40px', right: '-40px', width: '140px', height: '140px', borderRadius: '50%', background: `radial-gradient(circle, ${glow} 0%, transparent 70%)`, pointerEvents: 'none', opacity: 0.85 }} />

      <div className="relative">
        <p className="font-semibold mb-3" style={{ color: 'var(--text-dim)', fontSize: '16px' }}>{label}</p>
        <p className="font-bold tabular-nums font-display" style={{ color: 'var(--text)', fontSize: '30px' }}>{value}</p>
        {(subValue || badge) && (
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {subValue && <p className="font-semibold" style={{ color, fontSize: '16px' }}>{subValue}</p>}
            {badge && (
              <span className="px-2 py-0.5 rounded-full" style={{ background: 'var(--surface)', color: 'var(--text-dim)', fontFamily: 'monospace', fontSize: '11px' }}>
                {badge}
              </span>
            )}
          </div>
        )}
      </div>

      {sparkData.length > 1 && (
        <div className="relative pt-3" style={{ height: '58px', marginLeft: '-4px', marginRight: '-4px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sparkData} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
              <defs>
                <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Tooltip content={<SparkTip />} cursor={{ stroke: color, strokeWidth: 1, strokeDasharray: '3 3' }} />
              <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2} fill={`url(#${gid})`} dot={false} isAnimationActive={firstRender.current} animationDuration={800} animationEasing="ease-out" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
